import { useEffect, useState } from 'react';
import type { ProvisioningListItem, ProvisioningStatus } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import type { AdminTranslator } from '../../i18n/index.js';
import { provisioningApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { formatDateTime } from '../../lib/format.js';

/** The device polls every ~30s (packages/pos/src/provisioning/loop.ts); refreshing the admin's list a couple of times in that window is enough for a newly-announcing device to show up without a manual reload, without hammering the endpoint. */
const POLL_INTERVAL_MS = 15000;

function formatDeviceInfo(t: AdminTranslator, device: ProvisioningListItem['deviceInfo']): string {
  if (!device) return t('terminals.deviceUnknown');
  const platformLine =
    device.platform && device.osVersion ? `${device.platform} ${device.osVersion}` : (device.platform ?? device.osVersion);
  const appLine = device.appVersionName ? `v${device.appVersionName}` : undefined;
  const parts = [device.model, platformLine, appLine].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : t('terminals.deviceUnknown');
}

function statusBadge(t: AdminTranslator, status: ProvisioningStatus): { kind: 'warn' | 'ok' | 'neutral'; label: string } {
  switch (status) {
    case 'pending':
      return { kind: 'warn', label: t('provisioning.statusPending') };
    case 'approved':
      return { kind: 'ok', label: t('provisioning.statusApproved') };
    case 'claimed':
      return { kind: 'ok', label: t('provisioning.statusClaimed') };
    case 'rejected':
      return { kind: 'neutral', label: t('provisioning.statusRejected') };
  }
}

function CopyableUuid({ value, name }: { value: string; name: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <span className="numeric" style={{ fontSize: 'var(--text-xs)' }}>
        {value}
      </span>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-label={t('provisioning.copyUuidLabel', { name })}
        onClick={async () => {
          await navigator.clipboard?.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? t('common.copied') : t('common.copy')}
      </button>
    </span>
  );
}

/**
 * "Dispositivos por vincular" — a distinct panel on the Terminales page
 * (not a separate route: this is a Terminales concern end to end, and a
 * second nav item for what is really one workflow split across two screens
 * would cost more than it buys — see
 * docs/decisions/2026-07-31-provisioning-admin-panel.md). An unpaired
 * device announces itself every ~30s and waits; this panel is how an admin
 * sees it, by name and full UUID, and approves or rejects it.
 */
export function ProvisioningPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState<ProvisioningListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function reload(silent = false) {
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await provisioningApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // Silent — a background refresh must never flash the loading state or
    // clobber whatever the admin is mid-typing into a rename field.
    const id = window.setInterval(() => void reload(true), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is stable in shape; re-creating the interval on every render would drift its cadence
  }, []);

  function flashStatus(message: string) {
    setStatus(message);
    window.setTimeout(() => setStatus((current) => (current === message ? null : current)), 5000);
  }

  function nameFor(row: ProvisioningListItem): string {
    return nameDrafts[row.terminalId] ?? row.name;
  }

  async function approve(row: ProvisioningListItem) {
    const draftName = nameFor(row).trim();
    if (!draftName) {
      setError(t('provisioning.nameRequired'));
      return;
    }
    setApprovingId(row.terminalId);
    setError(null);
    try {
      // Omitted (not merely sent-unchanged) when the admin left the
      // announced name alone — matches the server's own "omit to keep the
      // announced name" contract instead of relying on a same-value PATCH
      // being a no-op.
      await provisioningApi.approve(row.terminalId, draftName === row.name ? {} : { name: draftName });
      setNameDrafts((prev) => {
        const next = { ...prev };
        delete next[row.terminalId];
        return next;
      });
      flashStatus(t('provisioning.approveSuccess'));
      await reload(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setApprovingId(null);
    }
  }

  const rejectTarget = rows.find((r) => r.terminalId === rejectTargetId) ?? null;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-bold)' }}>{t('provisioning.title')}</h2>
        <p className="field-hint">{t('provisioning.subtitle')}</p>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}
      {status && <Banner kind="ok">{status}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p>{t('provisioning.empty')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('common.status')}</th>
                <th>{t('provisioning.name')}</th>
                <th>{t('provisioning.uuid')}</th>
                <th>{t('provisioning.device')}</th>
                <th className="numeric">{t('provisioning.firstSeen')}</th>
                <th className="numeric">{t('provisioning.lastSeen')}</th>
                <th>{t('provisioning.online')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const badge = statusBadge(t, row.status);
                const editable = row.status === 'pending';
                const canReject = row.status === 'pending' || row.status === 'approved';
                const muted = row.status === 'rejected';
                return (
                  <tr key={row.terminalId} className={muted ? 'row--muted' : undefined}>
                    <td>
                      <span className={`badge badge--${badge.kind}`}>{badge.label}</span>
                    </td>
                    <td>
                      {editable ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                          <input
                            value={nameFor(row)}
                            onChange={(e) =>
                              setNameDrafts((prev) => ({ ...prev, [row.terminalId]: e.target.value }))
                            }
                            disabled={approvingId === row.terminalId}
                            aria-label={t('provisioning.fieldLabel', { field: t('provisioning.name'), name: row.name })}
                            style={{ minWidth: '12rem' }}
                          />
                          <span className="field-hint">{t('provisioning.nameHint')}</span>
                        </div>
                      ) : (
                        row.name
                      )}
                    </td>
                    <td>
                      <CopyableUuid value={row.terminalId} name={row.name} />
                    </td>
                    <td>{formatDeviceInfo(t, row.deviceInfo)}</td>
                    <td className="numeric">{formatDateTime(row.firstSeenAt)}</td>
                    <td className="numeric">{formatDateTime(row.lastSeenAt)}</td>
                    <td>
                      <span className={`badge badge--${row.online ? 'ok' : 'neutral'}`}>
                        {row.online ? t('provisioning.online') : t('provisioning.offline')}
                      </span>
                    </td>
                    <td style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {row.status === 'pending' && (
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          disabled={approvingId === row.terminalId || !nameFor(row).trim()}
                          onClick={() => void approve(row)}
                        >
                          {approvingId === row.terminalId ? t('provisioning.approving') : t('provisioning.approve')}
                        </button>
                      )}
                      {canReject && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setRejectTargetId(row.terminalId)}
                        >
                          {t('provisioning.reject')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rejectTarget && (
        <ConfirmDialog
          title={t('provisioning.reject')}
          consequence={t('provisioning.rejectConfirm')}
          danger
          busy={rejecting}
          onCancel={() => setRejectTargetId(null)}
          onConfirm={async () => {
            setRejecting(true);
            setError(null);
            try {
              await provisioningApi.reject(rejectTarget.terminalId);
              setRejectTargetId(null);
              flashStatus(t('common.saved'));
              await reload(true);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
              setRejectTargetId(null);
            } finally {
              setRejecting(false);
            }
          }}
        />
      )}
    </div>
  );
}
